import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Edit, Trash2, Save, X, Target, Globe, Eye, AlertCircle, CheckCircle, Copy } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import SeedSeoButton from '@/components/admin/SeedSeoButton';

interface SeoSetting {
  id: string;
  page_type: string;
  page_identifier?: string;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
  created_at: string;
  updated_at: string;
}

interface NewSeoSetting {
  page_type: string;
  page_identifier: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  og_title: string;
  og_description: string;
  og_image: string;
}

const AdminSEO = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddingSeo, setIsAddingSeo] = useState(false);
  const [editingSeo, setEditingSeo] = useState<SeoSetting | null>(null);
  const [selectedPageType, setSelectedPageType] = useState<string>('all');
  const [newSeo, setNewSeo] = useState<NewSeoSetting>({
    page_type: 'home',
    page_identifier: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
  });
  const queryClient = useQueryClient();

  const pageTypes = [
    { value: 'home', label: 'Home Page', description: 'Main landing page' },
    { value: 'category', label: 'Category Page', description: 'Game category pages' },
    { value: 'game', label: 'Game Page', description: 'Individual game pages' },
    { value: 'custom', label: 'Custom Page', description: 'Static content pages' },
    { value: 'trending', label: 'Trending Page', description: 'Trending games page' },
  ];

  const seoTips = [
    { icon: CheckCircle, text: "Meta titles should be 50-60 characters", color: "text-green-600" },
    { icon: CheckCircle, text: "Meta descriptions should be 150-160 characters", color: "text-green-600" },
    { icon: AlertCircle, text: "Include target keywords naturally", color: "text-blue-600" },
    { icon: AlertCircle, text: "OG images should be 1200x630px", color: "text-blue-600" },
  ];

  const { data: seoSettings = [], isLoading, error } = useQuery({
    queryKey: ['admin-seo', searchTerm, selectedPageType],
    queryFn: async () => {
      try {
        let query = (supabase as any)
          .from('seo_settings')
          .select('*')
          .order('created_at', { ascending: false });

        if (searchTerm) {
          query = query.or(`page_type.ilike.%${searchTerm}%,meta_title.ilike.%${searchTerm}%,page_identifier.ilike.%${searchTerm}%`);
        }

        if (selectedPageType !== 'all') {
          query = query.eq('page_type', selectedPageType);
        }

        const { data, error } = await query;
        if (error) throw error;
        return (data || []) as SeoSetting[];
      } catch (err) {
        console.error('Failed to fetch SEO settings:', err);
        throw err;
      }
    },
    retry: 2,
    staleTime: 30000,
  });

  const createSeoMutation = useMutation({
    mutationFn: async (seo: NewSeoSetting) => {
      const { error } = await (supabase as any)
        .from('seo_settings')
        .insert([seo]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-seo'] });
      toast.success('SEO setting created successfully');
      setIsAddingSeo(false);
      resetForm();
    },
    onError: (error: any) => {
      console.error('Create SEO error:', error);
      toast.error(`Failed to create SEO setting: ${error.message || 'Unknown error'}`);
    },
  });

  const updateSeoMutation = useMutation({
    mutationFn: async (seo: SeoSetting) => {
      const { error } = await (supabase as any)
        .from('seo_settings')
        .update({
          page_type: seo.page_type,
          page_identifier: seo.page_identifier,
          meta_title: seo.meta_title,
          meta_description: seo.meta_description,
          meta_keywords: seo.meta_keywords,
          og_title: seo.og_title,
          og_description: seo.og_description,
          og_image: seo.og_image,
        })
        .eq('id', seo.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-seo'] });
      toast.success('SEO setting updated successfully');
      setEditingSeo(null);
    },
    onError: (error: any) => {
      console.error('Update SEO error:', error);
      toast.error(`Failed to update SEO setting: ${error.message || 'Unknown error'}`);
    },
  });

  const deleteSeoMutation = useMutation({
    mutationFn: async (seoId: string) => {
      const { error } = await (supabase as any)
        .from('seo_settings')
        .delete()
        .eq('id', seoId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-seo'] });
      toast.success('SEO setting deleted successfully');
    },
    onError: (error: any) => {
      console.error('Delete SEO error:', error);
      toast.error(`Failed to delete SEO setting: ${error.message || 'Unknown error'}`);
    },
  });

  const resetForm = () => {
    setNewSeo({
      page_type: 'home',
      page_identifier: '',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      og_title: '',
      og_description: '',
      og_image: '',
    });
  };

  const handleCreateSeo = () => {
    if (!newSeo.page_type) {
      toast.error('Page type is required');
      return;
    }
    if (!newSeo.meta_title) {
      toast.error('Meta title is required');
      return;
    }
    createSeoMutation.mutate(newSeo);
  };

  const handleUpdateSeo = () => {
    if (!editingSeo) return;
    updateSeoMutation.mutate(editingSeo);
  };

  const handleDeleteSeo = (seoId: string) => {
    if (window.confirm('Are you sure you want to delete this SEO setting?')) {
      deleteSeoMutation.mutate(seoId);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getCharacterCount = (text: string) => text?.length || 0;

  const getCharacterStatus = (text: string, min: number, max: number) => {
    const count = getCharacterCount(text);
    if (count === 0) return 'text-gray-400';
    if (count < min) return 'text-yellow-600';
    if (count > max) return 'text-red-600';
    return 'text-green-600';
  };

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <Card className="p-6 text-center">
          <h3 className="text-lg font-semibold text-red-600 mb-2">Error Loading SEO Settings</h3>
          <p className="text-gray-600 mb-4">{error.message || 'Failed to load SEO settings'}</p>
          <Button onClick={() => queryClient.invalidateQueries({ queryKey: ['admin-seo'] })}>
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">SEO Management</h1>
          <p className="text-gray-600 mt-1">Optimize your website for search engines</p>
        </div>
        <div className="flex gap-2 w-full lg:w-auto">
          <SeedSeoButton />
          <Dialog open={isAddingSeo} onOpenChange={setIsAddingSeo}>
            <DialogTrigger asChild>
              <Button className="flex-1 lg:flex-none bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700">
                <Plus className="w-4 h-4 mr-2" />
                Add SEO Setting
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add SEO Setting</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
                <div className="lg:col-span-2 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="page_type">Page Type *</Label>
                      <select
                        id="page_type"
                        value={newSeo.page_type}
                        onChange={(e) => setNewSeo(prev => ({ ...prev, page_type: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {pageTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="page_identifier">Page Identifier</Label>
                      <Input
                        id="page_identifier"
                        value={newSeo.page_identifier}
                        onChange={(e) => setNewSeo(prev => ({ ...prev, page_identifier: e.target.value }))}
                        placeholder="category-slug or game-id"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="meta_title">Meta Title *</Label>
                    <Input
                      id="meta_title"
                      value={newSeo.meta_title}
                      onChange={(e) => setNewSeo(prev => ({ ...prev, meta_title: e.target.value }))}
                      placeholder="SEO title for search results"
                    />
                    <div className={`text-xs ${getCharacterStatus(newSeo.meta_title, 50, 60)}`}>
                      {getCharacterCount(newSeo.meta_title)}/60 characters (optimal: 50-60)
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="meta_description">Meta Description</Label>
                    <Textarea
                      id="meta_description"
                      value={newSeo.meta_description}
                      onChange={(e) => setNewSeo(prev => ({ ...prev, meta_description: e.target.value }))}
                      placeholder="Brief description for search results"
                      rows={3}
                    />
                    <div className={`text-xs ${getCharacterStatus(newSeo.meta_description, 150, 160)}`}>
                      {getCharacterCount(newSeo.meta_description)}/160 characters (optimal: 150-160)
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="meta_keywords">Meta Keywords</Label>
                    <Input
                      id="meta_keywords"
                      value={newSeo.meta_keywords}
                      onChange={(e) => setNewSeo(prev => ({ ...prev, meta_keywords: e.target.value }))}
                      placeholder="keyword1, keyword2, keyword3"
                    />
                    <div className="text-xs text-gray-500">
                      Separate keywords with commas
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="og_title">Open Graph Title</Label>
                    <Input
                      id="og_title"
                      value={newSeo.og_title}
                      onChange={(e) => setNewSeo(prev => ({ ...prev, og_title: e.target.value }))}
                      placeholder="Title for social media sharing"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="og_description">Open Graph Description</Label>
                    <Textarea
                      id="og_description"
                      value={newSeo.og_description}
                      onChange={(e) => setNewSeo(prev => ({ ...prev, og_description: e.target.value }))}
                      placeholder="Description for social media sharing"
                      rows={3}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="og_image">Open Graph Image URL</Label>
                    <Input
                      id="og_image"
                      value={newSeo.og_image}
                      onChange={(e) => setNewSeo(prev => ({ ...prev, og_image: e.target.value }))}
                      placeholder="https://example.com/image.jpg"
                    />
                    <div className="text-xs text-gray-500">
                      Recommended: 1200x630px, under 8MB
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">SEO Tips</h4>
                  {seoTips.map((tip, index) => {
                    const Icon = tip.icon;
                    return (
                      <div key={index} className="flex items-start space-x-3">
                        <Icon className={`w-5 h-5 mt-0.5 ${tip.color}`} />
                        <span className="text-sm text-gray-600">{tip.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2 mt-6">
                <Button 
                  onClick={handleCreateSeo} 
                  disabled={createSeoMutation.isPending}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {createSeoMutation.isPending ? 'Creating...' : 'Create Setting'}
                </Button>
                <Button variant="outline" onClick={() => setIsAddingSeo(false)}>
                  Cancel
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 sm:p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search SEO settings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={selectedPageType}
            onChange={(e) => setSelectedPageType(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Page Types</option>
            {pageTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {/* SEO Settings Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Page</TableHead>
                <TableHead className="hidden md:table-cell">Meta Title</TableHead>
                <TableHead className="hidden lg:table-cell">Description</TableHead>
                <TableHead className="hidden lg:table-cell">Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : seoSettings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No SEO settings found
                  </TableCell>
                </TableRow>
              ) : (
                seoSettings.map((seo) => (
                  <TableRow key={seo.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Target className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 capitalize">{seo.page_type}</div>
                          {seo.page_identifier && (
                            <Badge variant="outline" className="text-xs mt-1">
                              {seo.page_identifier}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {seo.meta_title || 'No meta title'}
                      </div>
                      {seo.meta_title && (
                        <div className={`text-xs ${getCharacterStatus(seo.meta_title, 50, 60)}`}>
                          {getCharacterCount(seo.meta_title)} chars
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="text-sm text-gray-600 max-w-xs truncate">
                        {seo.meta_description || 'No description'}
                      </div>
                      {seo.meta_description && (
                        <div className={`text-xs ${getCharacterStatus(seo.meta_description, 150, 160)}`}>
                          {getCharacterCount(seo.meta_description)} chars
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="text-sm text-gray-600">
                        {formatDate(seo.created_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingSeo(seo)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(seo.meta_title || '')}
                          className="text-gray-600 hover:text-gray-700"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteSeo(seo.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Edit Dialog */}
      {editingSeo && (
        <Dialog open={!!editingSeo} onOpenChange={() => setEditingSeo(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit SEO Setting</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
              <div className="lg:col-span-2 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit_page_type">Page Type</Label>
                    <select
                      id="edit_page_type"
                      value={editingSeo.page_type}
                      onChange={(e) => setEditingSeo(prev => prev ? { ...prev, page_type: e.target.value } : null)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {pageTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit_page_identifier">Page Identifier</Label>
                    <Input
                      id="edit_page_identifier"
                      value={editingSeo.page_identifier || ''}
                      onChange={(e) => setEditingSeo(prev => prev ? { ...prev, page_identifier: e.target.value } : null)}
                      placeholder="category-slug or game-id"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_meta_title">Meta Title</Label>
                  <Input
                    id="edit_meta_title"
                    value={editingSeo.meta_title || ''}
                    onChange={(e) => setEditingSeo(prev => prev ? { ...prev, meta_title: e.target.value } : null)}
                    placeholder="SEO title for search results"
                  />
                  <div className={`text-xs ${getCharacterStatus(editingSeo.meta_title || '', 50, 60)}`}>
                    {getCharacterCount(editingSeo.meta_title || '')}/60 characters (optimal: 50-60)
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_meta_description">Meta Description</Label>
                  <Textarea
                    id="edit_meta_description"
                    value={editingSeo.meta_description || ''}
                    onChange={(e) => setEditingSeo(prev => prev ? { ...prev, meta_description: e.target.value } : null)}
                    placeholder="Brief description for search results"
                    rows={3}
                  />
                  <div className={`text-xs ${getCharacterStatus(editingSeo.meta_description || '', 150, 160)}`}>
                    {getCharacterCount(editingSeo.meta_description || '')}/160 characters (optimal: 150-160)
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_meta_keywords">Meta Keywords</Label>
                  <Input
                    id="edit_meta_keywords"
                    value={editingSeo.meta_keywords || ''}
                    onChange={(e) => setEditingSeo(prev => prev ? { ...prev, meta_keywords: e.target.value } : null)}
                    placeholder="keyword1, keyword2, keyword3"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_og_title">Open Graph Title</Label>
                  <Input
                    id="edit_og_title"
                    value={editingSeo.og_title || ''}
                    onChange={(e) => setEditingSeo(prev => prev ? { ...prev, og_title: e.target.value } : null)}
                    placeholder="Title for social media sharing"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_og_description">Open Graph Description</Label>
                  <Textarea
                    id="edit_og_description"
                    value={editingSeo.og_description || ''}
                    onChange={(e) => setEditingSeo(prev => prev ? { ...prev, og_description: e.target.value } : null)}
                    placeholder="Description for social media sharing"
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_og_image">Open Graph Image URL</Label>
                  <Input
                    id="edit_og_image"
                    value={editingSeo.og_image || ''}
                    onChange={(e) => setEditingSeo(prev => prev ? { ...prev, og_image: e.target.value } : null)}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">SEO Tips</h4>
                {seoTips.map((tip, index) => {
                  const Icon = tip.icon;
                  return (
                    <div key={index} className="flex items-start space-x-3">
                      <Icon className={`w-5 h-5 mt-0.5 ${tip.color}`} />
                      <span className="text-sm text-gray-600">{tip.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button 
                onClick={handleUpdateSeo} 
                disabled={updateSeoMutation.isPending}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {updateSeoMutation.isPending ? 'Updating...' : 'Update Setting'}
              </Button>
              <Button variant="outline" onClick={() => setEditingSeo(null)}>
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdminSEO;
